/**
 * Performance Page - comprehensive portfolio performance visualization.
 * Displays returns chart, drawdown, PnL distribution, and monthly heatmap.
 */

import { useState, useCallback } from 'react';
import { MainLayout } from '@/components/layout';
import { Card, CardHeader, CardTitle, CardContent, Button, Spinner } from '@/components/ui';
import { AccountSelector } from '@/components/dashboard';
import {
  TimeFrameSelector,
  PerformanceSummary,
  ReturnsChart,
  DrawdownChart,
  MonthlyReturnsHeatmap,
  PnLDistributionChart,
  ExportButton,
} from '@/components/performance';
import { useAccounts, usePerformanceData, usePnLDistribution, useExportPerformance } from '@/hooks';
import { findMaxDrawdown } from '@/utils/performanceCalculations';
import { formatDateTime } from '@/utils/formatters';
import type { ExtendedTimeRange } from '@/types/performance';

export function PerformancePage() {
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [timeFrame, setTimeFrame] = useState<ExtendedTimeRange>('1m');
  const [showBenchmark, setShowBenchmark] = useState(false);

  const { accounts, isLoading: accountsLoading } = useAccounts();

  const {
    data: performanceData,
    stats,
    drawdownData,
    monthlyReturns,
    isLoading: performanceLoading,
    error: performanceError,
    refresh,
    lastUpdated,
  } = usePerformanceData(selectedAccountId, {
    timeFrame,
    includeBenchmark: showBenchmark,
  });

  const {
    buckets,
    winRate,
    meanPnl,
    medianPnl,
    isLoading: pnlLoading,
  } = usePnLDistribution(selectedAccountId);

  const { exportCSV, isExporting } = useExportPerformance(
    selectedAccountId ?? '',
    timeFrame,
    performanceData
  );

  const handleAccountSelect = useCallback((accountId: string) => {
    setSelectedAccountId(accountId);
  }, []);

  // Auto-select first account if none selected
  if (!selectedAccountId && accounts.length > 0 && !accountsLoading) {
    const firstAccount = accounts[0];
    if (firstAccount) {
      setSelectedAccountId(firstAccount.account_id);
    }
  }

  const maxDrawdown = findMaxDrawdown(drawdownData);
  const isLoading = accountsLoading || (selectedAccountId && performanceLoading);
  const hasData = performanceData.length > 0;

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-dark-100">Performance</h1>
            <AccountSelector
              accounts={accounts}
              selectedAccountId={selectedAccountId}
              onSelect={handleAccountSelect}
              isLoading={accountsLoading}
            />
          </div>
          <div className="flex items-center gap-3">
            <TimeFrameSelector
              value={timeFrame}
              onChange={setTimeFrame}
              disabled={!selectedAccountId || !!isLoading}
            />
            <ExportButton
              onExport={exportCSV}
              isExporting={isExporting}
              disabled={!selectedAccountId || !hasData}
            />
          </div>
        </div>

        {/* Data accuracy warning */}
        <div className="flex items-start gap-3 rounded-lg border border-amber-700/50 bg-amber-900/20 px-4 py-3">
          <svg className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-amber-300">Under Development</p>
            <p className="text-xs text-amber-400/80 mt-0.5">
              Performance data shown here is estimated and not yet connected to live portfolio values.
              Accurate returns, drawdown, and daily P&L require net liquidating value data from the broker API, which has not been integrated yet.
            </p>
          </div>
        </div>

        {/* Last updated + refresh */}
        {selectedAccountId && lastUpdated && (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="relative">
                <div className="h-2 w-2 rounded-full bg-emerald-400" />
                <div className="absolute inset-0 h-2 w-2 animate-ping rounded-full bg-emerald-400 opacity-75" />
              </div>
              <span className="text-sm text-dark-400">
                {formatDateTime(lastUpdated)}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              disabled={!!isLoading}
              glow
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Refreshing...
                </>
              ) : (
                <>
                  <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </>
              )}
            </Button>
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <Spinner size="lg" />
              <p className="text-dark-400 text-sm">Loading performance data...</p>
            </div>
          </div>
        )}

        {/* No account selected */}
        {!isLoading && !selectedAccountId && (
          <Card className="border-dashed border-dark-600">
            <CardContent className="py-12 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-dark-800 flex items-center justify-center mb-4">
                <svg className="h-6 w-6 text-dark-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <p className="text-dark-400">Select an account to view performance data</p>
            </CardContent>
          </Card>
        )}

        {/* Error state */}
        {!isLoading && selectedAccountId && performanceError && (
          <Card className="border-red-800">
            <CardContent className="py-8 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-red-900/30 flex items-center justify-center mb-4">
                <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <p className="text-red-300 mb-4">Unable to load performance data. Please try again.</p>
              <Button variant="outline" size="sm" onClick={refresh}>
                Retry
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Empty state */}
        {!isLoading && selectedAccountId && !performanceError && !hasData && (
          <Card className="border-dashed border-dark-600">
            <CardContent className="py-12 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-dark-800 flex items-center justify-center mb-4">
                <svg className="h-6 w-6 text-dark-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <p className="text-dark-400">No performance data available for this time period.</p>
              <p className="text-dark-500 text-sm mt-2">Try selecting a different time frame.</p>
            </CardContent>
          </Card>
        )}

        {/* Performance content */}
        {!isLoading && selectedAccountId && !performanceError && hasData && (
          <>
            {/* Performance Summary */}
            <PerformanceSummary stats={stats} isLoading={performanceLoading} />

            {/* Returns Chart - full width */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Cumulative Returns</CardTitle>
                <label className="flex items-center gap-2 text-sm text-dark-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showBenchmark}
                    onChange={(e) => setShowBenchmark(e.target.checked)}
                    className="rounded border-dark-600 bg-dark-800 text-primary-600 focus:ring-primary-500"
                  />
                  Show Benchmark (S&P 500)
                </label>
              </CardHeader>
              <CardContent>
                <ReturnsChart
                  data={performanceData}
                  showBenchmark={showBenchmark}
                  benchmarkName="S&P 500"
                  isLoading={performanceLoading}
                />
              </CardContent>
            </Card>

            {/* Two-column grid: Drawdown | PnL Distribution */}
            <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Drawdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <DrawdownChart
                    data={drawdownData}
                    maxDrawdown={maxDrawdown}
                    isLoading={performanceLoading}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>PnL Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <PnLDistributionChart
                    data={buckets}
                    winRate={winRate}
                    meanPnl={meanPnl}
                    medianPnl={medianPnl}
                    isLoading={pnlLoading}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Monthly Returns Heatmap - full width */}
            <Card>
              <CardHeader>
                <CardTitle>Monthly Returns</CardTitle>
              </CardHeader>
              <CardContent>
                <MonthlyReturnsHeatmap
                  data={monthlyReturns}
                  isLoading={performanceLoading}
                />
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </MainLayout>
  );
}
